import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminMeetingRooms.css';

function AdminMeetingRooms({ user }) {
    const [rooms, setRooms] = useState([]);
    const [devices, setDevices] = useState([]);
    const [todayBookings, setTodayBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState({
        booth_id: '',
        label: '',
        background_image: null,
        status: 'available',
        blocked_reason: '',
        blocked_until: ''
    });
    const [imagePreview, setImagePreview] = useState(null);

    // Bookings state
    const [allBookings, setAllBookings] = useState([]);
    const [bookingsFilter, setBookingsFilter] = useState('all');
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [bookingsSearchTerm, setBookingsSearchTerm] = useState('');
    const [bookingsStartDate, setBookingsStartDate] = useState('');
    const [bookingsEndDate, setBookingsEndDate] = useState('');
    const [bookingsPage, setBookingsPage] = useState(1);
    const bookingsPerPage = 20;

    // Booking form state (for create/edit)
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    const [bookingFormData, setBookingFormData] = useState({
        room_id: '',
        user_email: '',
        booking_date: '',
        start_time: '',
        end_time: '',
        meeting_title: ''
    });

    // Enroll device state
    const [deviceId, setDeviceId] = useState('');
    const [selectedRoom, setSelectedRoom] = useState('');
    const [enrolling, setEnrolling] = useState(false);

    const fetchRooms = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/meeting-rooms');
            setRooms(response.data || []);
        } catch (err) {
            setError('Failed to load meeting rooms');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDevices = useCallback(async () => {
        try {
            const response = await axios.get('/api/admin/devices');
            const roomDisplays = (response.data || []).filter(d => d.type === 'room_display');
            setDevices(roomDisplays);
        } catch (err) {
            console.error('Failed to load devices');
        }
    }, []);

    const fetchTodayBookings = useCallback(async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const response = await axios.get(`/api/meeting-rooms/bookings/date/${today}`);
            setTodayBookings(response.data || []);
        } catch (err) {
            console.error('Failed to load today bookings');
        }
    }, []);

    const fetchBookings = useCallback(async () => {
        try {
            setLoadingBookings(true);
            const params = new URLSearchParams();
            if (bookingsFilter !== 'all') {
                params.append('status', bookingsFilter);
            }
            if (bookingsStartDate) {
                params.append('date_from', bookingsStartDate);
            }
            if (bookingsEndDate) {
                params.append('date_to', bookingsEndDate);
            }
            const response = await axios.get(`/api/meeting-rooms/bookings/all?${params}`);
            setAllBookings(response.data || []);
        } catch (err) {
            console.error('Failed to load bookings:', err);
        } finally {
            setLoadingBookings(false);
        }
    }, [bookingsFilter, bookingsStartDate, bookingsEndDate]);

    useEffect(() => {
        fetchRooms();
        fetchDevices();
        fetchTodayBookings();
        fetchBookings();
    }, [fetchRooms, fetchDevices, fetchTodayBookings, fetchBookings]);

    useEffect(() => {
        fetchBookings();
    }, [bookingsFilter, fetchBookings]);

    // Create or update meeting room
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            // Use booth_id as-is (supports PB-, CPod-, MR- prefixes)
            const boothId = formData.booth_id.trim().toUpperCase();

            // Create FormData for multipart upload
            const submitData = new FormData();
            submitData.append('booth_id', boothId);
            submitData.append('label', formData.label);

            if (formData.background_image) {
                submitData.append('background_image', formData.background_image);
            }

            // Add blocking fields
            submitData.append('status', formData.status || 'available');
            if (formData.status === 'blocked') {
                if (formData.blocked_until) {
                    submitData.append('blocked_until', formData.blocked_until);
                }
                if (formData.blocked_reason) {
                    submitData.append('blocked_reason', formData.blocked_reason);
                }
            }

            if (editingRoom) {
                // Update existing room
                await axios.put(`/api/admin/kiosks/${editingRoom.id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                // Create new room
                await axios.post('/api/admin/kiosks', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setShowForm(false);
            setEditingRoom(null);
            setFormData({
                booth_id: '',
                label: '',
                background_image: null,
                status: 'available',
                blocked_reason: '',
                blocked_until: ''
            });
            setImagePreview(null);
            fetchRooms();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save meeting room');
        }
    };

    // Delete meeting room
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this meeting room?')) return;

        try {
            await axios.delete(`/api/admin/kiosks/${id}`);
            fetchRooms();
            fetchDevices();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete meeting room');
        }
    };

    // Edit meeting room
    const handleEdit = (room) => {
        setEditingRoom(room);
        setFormData({
            booth_id: room.booth_id, // Keep full booth_id (PB-, CPod-, MR-)
            label: room.label || '',
            background_image: null,
            status: room.status || 'available',
            blocked_reason: room.blocked_reason || '',
            blocked_until: room.blocked_until || ''
        });
        setImagePreview(room.background_image || null);
        setShowForm(true);
    };

    // Cancel booking
    const handleCancelBooking = async (bookingId) => {
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;

        try {
            await axios.delete(`/api/meeting-rooms/bookings/${bookingId}`);
            fetchBookings(); // Refresh list
            alert('Booking cancelled successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to cancel booking');
        }
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setError('Image file size should be less than 5MB');
                return;
            }
            setFormData({ ...formData, background_image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // Create or update booking
    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            if (editingBooking) {
                // Update existing booking
                await axios.put(`/api/meeting-rooms/bookings/${editingBooking.id}`, bookingFormData);
                alert('Booking updated successfully');
            } else {
                // Create new booking
                await axios.post('/api/meeting-rooms/book', bookingFormData);
                alert('Booking created successfully');
            }
            setShowBookingForm(false);
            setEditingBooking(null);
            setBookingFormData({
                room_id: '',
                user_email: '',
                booking_date: '',
                start_time: '',
                end_time: '',
                meeting_title: ''
            });
            fetchBookings();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save booking');
        }
    };

    // Edit booking
    const handleEditBooking = (booking) => {
        setEditingBooking(booking);
        setBookingFormData({
            room_id: booking.room_id,
            user_email: booking.user_email,
            booking_date: booking.booking_date,
            start_time: booking.start_time,
            end_time: booking.end_time,
            meeting_title: booking.meeting_title || ''
        });
        setShowBookingForm(true);
    };

    // Enroll device
    const handleEnrollDevice = async (e) => {
        e.preventDefault();
        if (!deviceId || !selectedRoom) return;

        setEnrolling(true);
        setError(null);

        try {
            await axios.post('/api/admin/devices/enroll', {
                device_id: deviceId,
                device_type: 'room_display',
                kiosk_id: selectedRoom
            });

            setDeviceId('');
            setSelectedRoom('');
            fetchDevices();
            alert('Device enrolled successfully!');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to enroll device');
        } finally {
            setEnrolling(false);
        }
    };

    // Get device for a room
    const getDeviceForRoom = (roomId) => {
        return devices.find(d => d.kiosk_id === roomId);
    };

    // Get today's bookings for a room
    const getBookingsForRoom = (roomId) => {
        return todayBookings.filter(b => b.room_id === roomId);
    };

    // Filter bookings by search term
    const filteredBookings = allBookings.filter(booking => {
        if (!bookingsSearchTerm) return true;
        const searchLower = bookingsSearchTerm.toLowerCase();
        return (
            booking.user_name?.toLowerCase().includes(searchLower) ||
            booking.user_email?.toLowerCase().includes(searchLower) ||
            booking.booth_id?.toLowerCase().includes(searchLower) ||
            booking.room_name?.toLowerCase().includes(searchLower) ||
            booking.meeting_title?.toLowerCase().includes(searchLower)
        );
    });

    // Pagination for bookings
    const indexOfLastBooking = bookingsPage * bookingsPerPage;
    const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
    const currentPageBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
    const totalBookingsPages = Math.ceil(filteredBookings.length / bookingsPerPage);

    if (loading) {
        return <div className="amr-loading">Loading meeting rooms...</div>;
    }

    return (
        <div className="admin-meeting-rooms">
            <div className="amr-header">
                <h2>🏢 Meeting Rooms</h2>
                <button
                    className="btn-add-room"
                    onClick={() => {
                        setShowForm(true);
                        setEditingRoom(null);
                        setFormData({ booth_id: '', label: '' });
                    }}
                >
                    + Add Meeting Room
                </button>
            </div>

            {error && <div className="amr-error">{error}</div>}

            {/* Add/Edit Form */}
            {showForm && (
                <div className="amr-form-overlay">
                    <div className="amr-form">
                        <h3>{editingRoom ? 'Edit Meeting Room' : 'Add Meeting Room'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Room ID</label>
                                <input
                                    type="text"
                                    value={formData.booth_id}
                                    onChange={(e) => setFormData({ ...formData, booth_id: e.target.value })}
                                    placeholder="PB-W1, CPod-E1, MR-1..."
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Room Name</label>
                                <input
                                    type="text"
                                    value={formData.label}
                                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                                    placeholder="Palu, Manado, Jakarta..."
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Background Image</label>
                                <div className="image-upload-container">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        id="background-upload"
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="background-upload" className="upload-button">
                                        📷 Choose Image
                                    </label>
                                    {imagePreview && (
                                        <div className="image-preview">
                                            <img src={imagePreview} alt="Preview" />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setImagePreview(null);
                                                    setFormData({ ...formData, background_image: null });
                                                }}
                                                className="remove-image"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <small style={{ color: '#666', fontSize: '12px' }}>Max size: 5MB. Recommended: 800x600px</small>
                            </div>

                            <div className="form-group">
                                <label>🔒 Availability Status</label>
                                <select
                                    value={formData.status || 'available'}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="available">✅ Available for Booking</option>
                                    <option value="blocked">🚫 Blocked</option>
                                </select>
                            </div>

                            {formData.status === 'blocked' && (
                                <>
                                    <div className="form-group">
                                        <label>Block Until (Optional)</label>
                                        <input
                                            type="datetime-local"
                                            value={formData.blocked_until || ''}
                                            onChange={(e) => setFormData({ ...formData, blocked_until: e.target.value })}
                                        />
                                        <small style={{ color: '#666', fontSize: '12px' }}>Leave empty to block indefinitely</small>
                                    </div>
                                    <div className="form-group">
                                        <label>Reason for Blocking</label>
                                        <input
                                            type="text"
                                            value={formData.blocked_reason || ''}
                                            onChange={(e) => setFormData({ ...formData, blocked_reason: e.target.value })}
                                            placeholder="e.g., Maintenance, Private Event, Cleaning"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="form-actions">
                                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
                                <button type="submit" className="btn-primary">
                                    {editingRoom ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Meeting Rooms Table */}
            <div className="amr-table-container">
                <table className="amr-table">
                    <thead>
                        <tr>
                            <th>Room ID</th>
                            <th>Room Name</th>
                            <th>Availability</th>
                            <th>Display Device</th>
                            <th>Today's Bookings</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rooms.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="empty-row">
                                    No meeting rooms found. Click "Add Meeting Room" to create one.
                                </td>
                            </tr>
                        ) : (
                            rooms.map(room => {
                                const device = getDeviceForRoom(room.id);
                                const bookings = getBookingsForRoom(room.id);

                                return (
                                    <tr key={room.id}>
                                        <td className="room-id">{room.booth_id}</td>
                                        <td className="room-name">{room.label}</td>
                                        <td className="room-status">
                                            {room.status === 'blocked' ? (
                                                <div className="status-blocked">
                                                    <span className="blocked-badge">🚫 Blocked</span>
                                                    {room.blocked_reason && (
                                                        <small className="block-reason">{room.blocked_reason}</small>
                                                    )}
                                                    {room.blocked_until && (
                                                        <small className="block-until">
                                                            Until: {new Date(room.blocked_until).toLocaleString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </small>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="status-available">✅ Available</span>
                                            )}
                                        </td>
                                        <td className="room-device">
                                            {device ? (
                                                <div className="device-info">
                                                    <span className="device-id">{device.device_id}</span>
                                                    <span className={`device-status ${device.last_heartbeat ? 'online' : 'offline'}`}>
                                                        {device.last_heartbeat ? '● Online' : '○ Offline'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="no-device">No device</span>
                                            )}
                                        </td>
                                        <td className="room-bookings">
                                            {bookings.length > 0 ? (
                                                <div className="bookings-list">
                                                    {bookings.slice(0, 3).map(b => (
                                                        <div key={b.id} className="booking-item">
                                                            {b.start_time}-{b.end_time}
                                                        </div>
                                                    ))}
                                                    {bookings.length > 3 && (
                                                        <div className="more-bookings">+{bookings.length - 3} more</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="no-bookings">No bookings</span>
                                            )}
                                        </td>
                                        <td className="room-actions">
                                            <button onClick={() => handleEdit(room)} className="btn-edit">Edit</button>
                                            <button onClick={() => handleDelete(room.id)} className="btn-delete">Delete</button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Enroll Device Section */}
            <div className="amr-enroll-section">
                <h3>📱 Enroll Room Display</h3>
                <form onSubmit={handleEnrollDevice} className="enroll-form">
                    <div className="form-group">
                        <label>Device ID</label>
                        <input
                            type="text"
                            value={deviceId}
                            onChange={(e) => setDeviceId(e.target.value)}
                            placeholder="android-xxxxxxxx"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Meeting Room</label>
                        <select
                            value={selectedRoom}
                            onChange={(e) => setSelectedRoom(e.target.value)}
                            required
                        >
                            <option value="">Select Room...</option>
                            {rooms.map(room => (
                                <option key={room.id} value={room.id}>
                                    {room.booth_id} - {room.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button type="submit" disabled={enrolling} className="btn-enroll">
                        {enrolling ? 'Enrolling...' : 'Enroll Device'}
                    </button>
                </form>
            </div>

            {/* Enrolled Devices List */}
            {devices.length > 0 && (
                <div className="amr-devices-section">
                    <h3>📺 Enrolled Room Displays</h3>
                    <div className="devices-grid">
                        {devices.map(device => {
                            const room = rooms.find(r => r.id === device.kiosk_id);
                            return (
                                <div key={device.device_id} className="device-card">
                                    <div className="device-header">
                                        <span className="device-id">{device.device_id}</span>
                                        <span className={`status-badge ${device.last_heartbeat ? 'online' : 'offline'}`}>
                                            {device.last_heartbeat ? 'Online' : 'Offline'}
                                        </span>
                                    </div>
                                    <div className="device-room">
                                        {room ? `${room.booth_id} - ${room.label}` : 'Unlinked'}
                                    </div>
                                    {device.last_heartbeat && (
                                        <div className="device-heartbeat">
                                            Last seen: {new Date(device.last_heartbeat).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Bookings Management Section */}
            <div className="amr-bookings-section" style={{ marginTop: '3rem' }}>
                <h3>📅 Meeting Room Bookings</h3>

                <div className="bookings-filters" style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
                        <input
                            type="text"
                            placeholder="Search by user, email, room, or title..."
                            value={bookingsSearchTerm}
                            onChange={(e) => setBookingsSearchTerm(e.target.value)}
                            style={{ flex: '1', minWidth: '250px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        <select
                            value={bookingsFilter}
                            onChange={(e) => setBookingsFilter(e.target.value)}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                            <option value="all">All Status</option>
                            <option value="confirmed">Confirmed Only</option>
                            <option value="cancelled">Cancelled Only</option>
                        </select>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <label style={{ fontSize: '14px', color: '#666' }}>From:</label>
                            <input
                                type="date"
                                value={bookingsStartDate}
                                onChange={(e) => setBookingsStartDate(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <label style={{ fontSize: '14px', color: '#666' }}>To:</label>
                            <input
                                type="date"
                                value={bookingsEndDate}
                                onChange={(e) => setBookingsEndDate(e.target.value)}
                                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <button
                            onClick={() => {
                                setBookingsSearchTerm('');
                                setBookingsStartDate('');
                                setBookingsEndDate('');
                                setBookingsFilter('all');
                            }}
                            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer' }}
                        >
                            Clear Filters
                        </button>
                        <button
                            onClick={() => {
                                setEditingBooking(null);
                                setBookingFormData({
                                    room_id: '',
                                    user_email: '',
                                    booking_date: '',
                                    start_time: '',
                                    end_time: '',
                                    meeting_title: ''
                                });
                                setShowBookingForm(true);
                            }}
                            className="btn-add-room"
                            style={{ padding: '8px 16px', marginLeft: 'auto' }}
                        >
                            + Create Booking
                        </button>
                    </div>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                        {loadingBookings ? 'Loading...' : `Showing ${filteredBookings.length} of ${allBookings.length} bookings`}
                    </div>
                </div>

                <div className="amr-table-container">
                    <table className="amr-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Room</th>
                                <th>Time</th>
                                <th>User</th>
                                <th>Title</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBookings.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="empty-row">
                                        {loadingBookings ? 'Loading bookings...' : 'No bookings found'}
                                    </td>
                                </tr>
                            ) : (
                                currentPageBookings.map(booking => (
                                    <tr key={booking.id}>
                                        <td>{new Date(booking.booking_date).toLocaleDateString()}</td>
                                        <td>
                                            <strong>{booking.booth_id}</strong>
                                            <br />
                                            <small style={{ color: '#666' }}>{booking.room_name}</small>
                                        </td>
                                        <td>{booking.start_time} - {booking.end_time}</td>
                                        <td>
                                            {booking.user_name || booking.user_email}
                                            <br />
                                            <small style={{ color: '#666' }}>{booking.user_email}</small>
                                        </td>
                                        <td>{booking.meeting_title || '—'}</td>
                                        <td>
                                            <span className={`status-badge status-${booking.status}`}>
                                                {booking.status}
                                            </span>
                                        </td>
                                        <td>
                                            {booking.status === 'confirmed' && (
                                                <>
                                                    <button
                                                        onClick={() => handleEditBooking(booking)}
                                                        className="btn-edit"
                                                        style={{ fontSize: '12px', padding: '4px 8px', marginRight: '4px' }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancelBooking(booking.id)}
                                                        className="btn-delete"
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {totalBookingsPages > 1 && (
                    <div className="bookings-pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem', gap: '1rem' }}>
                        <button
                            onClick={() => setBookingsPage(prev => Math.max(prev - 1, 1))}
                            disabled={bookingsPage === 1}
                            style={{
                                padding: '8px 16px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                background: bookingsPage === 1 ? '#f5f5f5' : 'white',
                                cursor: bookingsPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Previous
                        </button>
                        <span style={{ color: '#666' }}>
                            Page {bookingsPage} of {totalBookingsPages}
                        </span>
                        <button
                            onClick={() => setBookingsPage(prev => Math.min(prev + 1, totalBookingsPages))}
                            disabled={bookingsPage === totalBookingsPages}
                            style={{
                                padding: '8px 16px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                background: bookingsPage === totalBookingsPages ? '#f5f5f5' : 'white',
                                cursor: bookingsPage === totalBookingsPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Booking Form Modal */}
            {showBookingForm && (
                <div className="amr-form-overlay">
                    <div className="amr-form">
                        <h3>{editingBooking ? 'Edit Booking' : 'Create Booking'}</h3>
                        <form onSubmit={handleBookingSubmit}>
                            <div className="form-group">
                                <label>Room *</label>
                                <select
                                    value={bookingFormData.room_id}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, room_id: e.target.value })}
                                    required
                                >
                                    <option value="">Select Room...</option>
                                    {rooms.map(room => (
                                        <option key={room.id} value={room.id}>
                                            {room.booth_id} - {room.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>User Email *</label>
                                <input
                                    type="email"
                                    value={bookingFormData.user_email}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, user_email: e.target.value })}
                                    placeholder="user@unicef.org"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Date *</label>
                                <input
                                    type="date"
                                    value={bookingFormData.booking_date}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, booking_date: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Start Time *</label>
                                <input
                                    type="time"
                                    value={bookingFormData.start_time}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, start_time: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>End Time *</label>
                                <input
                                    type="time"
                                    value={bookingFormData.end_time}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, end_time: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Meeting Title (Optional)</label>
                                <input
                                    type="text"
                                    value={bookingFormData.meeting_title}
                                    onChange={(e) => setBookingFormData({ ...bookingFormData, meeting_title: e.target.value })}
                                    placeholder="Team Meeting"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setShowBookingForm(false)} className="btn-cancel">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-submit">
                                    {editingBooking ? 'Update Booking' : 'Create Booking'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminMeetingRooms;
